import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import "./OrderDocsModal.css";
import { getPedidoDocs } from "../../services/cliente/obtenerDocsPedidos";
import { fileURL } from "../../utils/fileURL";

/* KB cuando es chico y MB cuando no. Antes era siempre MB, asi que una
   factura de 40 KB se anunciaba como "0.04 MB" y una de 3 KB como
   "0.00 MB", que se lee como un archivo vacio. */
const formatSize = (bytes) => {
  const n = Number(bytes || 0);
  if (!Number.isFinite(n) || n <= 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const normalizePath = (p) => {
  if (!p) return null;
  let s = String(p).trim().replace(/\\/g, "/").replace(/^\/+/, "");
  const idx = s.toLowerCase().indexOf("uploads/");
  if (idx >= 0) s = s.slice(idx);
  return s;
};

/* La extension, para decir de que tipo es cada archivo sin abrirlo. */
const extensionDe = (nombre) => {
  const m = String(nombre || "").match(/\.([a-z0-9]{1,5})$/i);
  return m ? m[1].toUpperCase() : "FILE";
};

function OrderDocsModal({ pedidoId, onClose }) {
  const modalRef = useRef(null);

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchDocs = useCallback(async () => {
    if (!pedidoId) return;
    try {
      setLoading(true);
      setError("");
      const list = await getPedidoDocs(pedidoId);
      setDocs(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      setError(e?.message || "Could not load documents.");
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [pedidoId]);

  useEffect(() => {
    fetchDocs();
  }, [fetchDocs]);

  const onOverlayMouseDown = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  useEffect(() => {
    const onKeyDown = (ev) => {
      if (ev.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    setTimeout(() => modalRef.current?.focus?.(), 0);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const getDocName = (d) => d?.original_name || d?.filename || "Document";
  const getDocSize = (d) => d?.size_bytes || d?.size || null;

  const getDocUrl = (d) => {
    const p = d?.storage_path || d?.path || d?.ruta || d?.file || d?.archivo;
    const normalized = normalizePath(p) || p;
    return normalized ? fileURL(normalized) : null;
  };

  const formatDate = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  };

  const sortedDocs = useMemo(() => {
    const arr = Array.isArray(docs) ? [...docs] : [];
    arr.sort((a, b) => {
      const da = new Date(a?.created_at || a?.fecha || 0).getTime() || 0;
      const db = new Date(b?.created_at || b?.fecha || 0).getTime() || 0;
      return db - da;
    });
    return arr;
  }, [docs]);

  const hasDocs = sortedDocs.length > 0;

  return (
    <div
      className="omx-docs-scope omx-docs-modal__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="omxDocsTitle"
      onMouseDown={onOverlayMouseDown}
    >
      <div className="omx-docs-modal" ref={modalRef} tabIndex={-1}>
        <div className="omx-docs-modal__header">
          <div>
            <div className="omx-docs-modal__title" id="omxDocsTitle">
              Documents
            </div>
            <div className="omx-docs-modal__sub">
              Order #{pedidoId} · Invoices and files attached to this order
            </div>
          </div>

          <button
            type="button"
            className="omx-docs-modal__close"
            onClick={onClose}
            disabled={loading}
            aria-label="Close"
            title="Close"
          >
            ✕
          </button>
        </div>

        <div className="omx-docs-modal__body">
          {error ? <p className="omx-docs-error">{error}</p> : null}

          {/* Se saco el titulo "Uploaded Documents" que iba adentro: el
              modal ya se llama Documents, y decirlo dos veces en la misma
              pantalla ocupaba un renglon para no agregar nada. El boton de
              recargar queda, arriba a la derecha. */}
          {loading ? (
            <p className="omx-docs-vacio">Loading documents…</p>
          ) : !hasDocs ? (
            <div className="omx-docs-vacio">
              <strong>No documents yet</strong>
              <span>
                Your invoice and any files for this order will appear here as
                soon as we attach them.
              </span>
            </div>
          ) : (
            <div className="omx-docs-items">
              {sortedDocs.map((d, idx) => {
                const url = getDocUrl(d);
                const name = getDocName(d);
                const fecha = formatDate(d?.created_at || d?.fecha || d?.createdAt);
                const size = formatSize(getDocSize(d));

                return (
                  <div key={d?.id ?? idx} className="omx-docs-item">
                    {/* El tipo de archivo a la izquierda: se ve de un
                        vistazo cual es el PDF de la factura sin tener que
                        leer el nombre entero. */}
                    <span className="omx-docs-ext">{extensionDe(name)}</span>

                    <div className="omx-docs-item__info">
                      <div className="omx-docs-item__name" title={name}>
                        {name}
                      </div>

                      {fecha || size ? (
                        <div className="omx-docs-item__meta">
                          {[fecha, size].filter(Boolean).join(" · ")}
                        </div>
                      ) : null}
                    </div>

                    {/* Un solo boton. Habia dos, "View" y "Download", con
                        el mismo href y el mismo target: hacian exactamente
                        lo mismo y sugerian una diferencia que no existia.
                        Para que bajara el archivo en vez de abrirlo hace
                        falta que el backend mande Content-Disposition, que
                        es otro cambio. */}
                    {url ? (
                      <a
                        className="omx-docs-abrir"
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open
                      </a>
                    ) : (
                      <span className="omx-docs-sinlink">Not available</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="omx-docs-modal__footer">
          <button
            type="button"
            className="omx-docs-btn omx-docs-btn--ghost"
            onClick={fetchDocs}
            disabled={loading}
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>

          <button
            type="button"
            className="omx-docs-btn"
            onClick={onClose}
            disabled={loading}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default OrderDocsModal;
